import * as Dialog from '@radix-ui/react-dialog'
import { cn } from '@repo/tailwind-config/tailwind.functions'
import { SVGFC } from '@repo/toolkit/types'
import { PropsWithChildren, useEffect, useRef, useState } from 'react'

import useTranslate from '../../../../../apps/backoffice/src/lib/hooks/use-translate'
import { DeleteIcon } from '../../../icons'
import Badge from '../../atoms/badge'
import Button from '../../atoms/button'
import NoteBox from '../../atoms/note-box'
import ModalHeader from './modal-header'

export type ActionButtonsVariants = 'regular' | 'delete' | 'form'

export type ModalVariants = 'regular' | 'confirmation' | 'destructive'

export type ModalSizes = 'small' | 'medium' | 'large' | 'full'

export type ActionButtonsOptions = {
  confirmText?: string
  cancelText?: string
  onConfirm?: () => void
  hideIcon?: boolean
}

type ModalProps = {
  isOpen: boolean
  title?: string
  titleAlignment?: 'center' | 'left'
  subTitle?: string
  icon?: SVGFC
  note?: string

  variant?: ModalVariants
  size?: ModalSizes

  customActions?: React.ReactNode
  headerChildren?: React.ReactNode
  className?: string

  hideHeader?: boolean
  hideActions?: boolean

  actionButtonsOptions?: ActionButtonsOptions

  hasBorderBottom?: boolean

  // lets the user move the modal by dragging its header (it stays inside the viewport)
  draggable?: boolean

  isLoading?: boolean
  disableOutSideClick?: boolean
  onCancel: () => void
}

const mapModalVariantToActionButtonsVariant: Record<ModalVariants, ActionButtonsVariants> = {
  destructive: 'delete',
  confirmation: 'regular',
  regular: 'form'
}

export const MODAL_BODY_CLASSNAME = 'modal-body'
export const MODAL_CONTENT_CLASSNAME = 'modal-content'

const Modal: React.FC<PropsWithChildren<ModalProps>> = ({
  isOpen,
  title,
  titleAlignment,
  subTitle,
  children,
  icon,
  note,

  size = 'small',
  variant = 'regular',

  hideActions,
  hideHeader,

  customActions,
  headerChildren,
  actionButtonsOptions: actionButtonsOptionsProp,
  isLoading,
  disableOutSideClick = false,
  className,
  hasBorderBottom,
  draggable,
  onCancel
}) => {
  const { t } = useTranslate()
  const actionButtonsOptions = actionButtonsOptionsProp ?? {
    cancelText: t('button.close'),
    confirmText: t('common.modal.accept')
  }
  const contentRef = useRef<HTMLDivElement>(null)
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 })

  useEffect(() => {
    if (isOpen) {
      document.body.setAttribute('data-dnd-disabled', 'true')
      return () => {
        document.body.removeAttribute('data-dnd-disabled')
      }
    }
  }, [isOpen])

  // every opening starts centered
  useEffect(() => {
    if (!isOpen) setDragOffset({ x: 0, y: 0 })
  }, [isOpen])

  const handleDragStart = (event: React.PointerEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement
    if (!draggable || event.button !== 0 || target.closest('button') || !contentRef.current) return

    const rect = contentRef.current.getBoundingClientRect()
    const startOffset = dragOffset
    const minX = startOffset.x - rect.left
    const maxX = startOffset.x + window.innerWidth - rect.right
    const minY = startOffset.y - rect.top
    const maxY = startOffset.y + window.innerHeight - rect.bottom

    const handleMove = (moveEvent: PointerEvent) => {
      setDragOffset({
        x: Math.min(Math.max(startOffset.x + moveEvent.clientX - event.clientX, minX), maxX),
        y: Math.min(Math.max(startOffset.y + moveEvent.clientY - event.clientY, minY), maxY)
      })
    }
    const handleUp = () => {
      window.removeEventListener('pointermove', handleMove)
      window.removeEventListener('pointerup', handleUp)
      window.removeEventListener('pointercancel', handleUp)
    }

    window.addEventListener('pointermove', handleMove)
    window.addEventListener('pointerup', handleUp)
    window.addEventListener('pointercancel', handleUp)
  }

  const buttonVariant = mapModalVariantToActionButtonsVariant[variant]
  const accessibleTitle = title?.trim() ? title : 'Modal'

  return (
    <Dialog.Root
      open={isOpen}
      onOpenChange={(open) => {
        if (!open && !isLoading) onCancel?.()
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay
          className="bg-black/40 data-[state=open]:animate-overlayShow fixed inset-0 z-30"
          id="overlay"
          style={{ pointerEvents: 'auto' }}
        />

        <div className="fixed inset-0 flex items-center justify-center z-30">
          <Dialog.Content
            ref={contentRef}
            // relative offset instead of a transform: a transform would become the containing
            // block of the fixed popups inside the modal (date pickers in "over" mode)
            style={
              draggable
                ? { position: 'relative', left: dragOffset.x, top: dragOffset.y }
                : undefined
            }
            className={cn(
              'max-h-[85vh] w-[90vw] rounded-xl bg-white focus shadow-lg z-50',
              className,
              { 'max-w-[32%] overflow-auto mobile:max-w-[100%]': size === 'small' },
              { 'max-w-[45%] overflow-auto mobile:max-w-[100%]': size === 'medium' },
              { 'max-w-[75%] overflow-auto mobile:max-w-[100%]': size === 'large' },
              { 'max-w-[90%] min-w-[1000px] overflow-auto': size === 'full' }
            )}
            onPointerDownOutside={(event) => {
              if (disableOutSideClick) event.preventDefault()
            }}
            onEscapeKeyDown={(event) => {
              if (disableOutSideClick) event.preventDefault()
            }}
          >
            <Dialog.Title className="sr-only">{accessibleTitle}</Dialog.Title>
            <Dialog.Description className="sr-only">
              {subTitle ?? note ?? `${accessibleTitle} dialog`}
            </Dialog.Description>
            {!hideHeader && (
              <div
                className={cn({ 'cursor-move select-none touch-none': draggable })}
                onPointerDown={handleDragStart}
              >
                <ModalHeader
                  title={title}
                  titleAlignment={titleAlignment}
                  icon={icon}
                  isIconCentered={variant === 'confirmation' || variant === 'destructive'}
                  isDestructive={variant === 'destructive'}
                  hasBorderBottom={hasBorderBottom}
                  onClose={() => {
                    if (!isLoading) onCancel()
                  }}
                >
                  {headerChildren}
                </ModalHeader>
              </div>
            )}
            <div onClick={(e) => e.stopPropagation()}>
              {subTitle && (
                <div className="p-5 pb-0">
                  <Badge text={subTitle} />
                </div>
              )}
              {note && (
                <div className={MODAL_BODY_CLASSNAME}>
                  <NoteBox note={note} />
                </div>
              )}
              {children}
            </div>
            {!hideActions && (
              <div className="p-5 pt-0">
                <div className={cn('flex flex-row justify-end items-center mt-5 gap-3')}>
                  <Button
                    disabled={isLoading}
                    variant="ghost"
                    type="button"
                    className={cn({
                      'flex-1': buttonVariant !== 'form'
                    })}
                    label={actionButtonsOptions?.cancelText}
                    aria-label={actionButtonsOptions?.cancelText}
                    onClick={onCancel}
                  />
                  {actionButtonsOptions?.onConfirm && (
                    <Button
                      loading={isLoading}
                      className={cn({
                        'flex-1': buttonVariant !== 'form'
                      })}
                      icon={
                        buttonVariant === 'delete'
                          ? actionButtonsOptions?.hideIcon
                            ? undefined
                            : DeleteIcon
                          : undefined
                      }
                      disabled={isLoading}
                      variant={buttonVariant === 'delete' ? 'destructive' : 'primary'}
                      label={actionButtonsOptions?.confirmText}
                      aria-label={actionButtonsOptions?.confirmText}
                      onClick={actionButtonsOptions?.onConfirm}
                    />
                  )}
                </div>
              </div>
            )}
            {customActions && <div className="p-5"> {customActions} </div>}
          </Dialog.Content>
        </div>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

export default Modal
